// Small looks-back built from the library and History (roadmap #14, #15):
// how long something has waited in the backlog, the backlog that has waited
// longest, and "On this day" — what you did on today's date in earlier years.

const DAY = 86400000;
const BACKLOG = new Set(["want_to_check", "haven_started"]);

// Days since an item was added (null when unknown).
export function backlogAgeDays(item, now = Date.now()) {
	const t = Date.parse(item?.created_at ?? "");
	return Number.isFinite(t) ? Math.max(0, Math.floor((now - t) / DAY)) : null;
}

// Backlog items that have waited at least `minDays`, oldest first. `snoozed`
// is a Map(item id → ISO date until which it's hidden).
export function staleBacklog(items, { now = Date.now(), minDays = 180, snoozed = new Map() } = {}) {
	return (items ?? [])
		.filter((m) => BACKLOG.has(m.engagement_status))
		.map((m) => ({ item: m, days: backlogAgeDays(m, now) }))
		.filter((x) => x.days != null && x.days >= minDays)
		.filter((x) => !(snoozed.get(x.item.id) && Date.parse(snoozed.get(x.item.id)) > now))
		.sort((a, b) => b.days - a.days);
}

// "On this day": History events on today's month-day in earlier years —
// finishes first, then starts, then additions — newest year first, one per item.
const KIND_RANK = (e) => (e.kind === "engagement" && e.to_value === "done" ? 0 : e.kind === "engagement" && e.to_value === "playing" ? 1 : e.kind === "added" ? 2 : 9);

export function onThisDay(events, today) {
	const md = today.slice(5, 10);
	const year = Number(today.slice(0, 4));
	const hits = (events ?? [])
		.filter((e) => !e.deleted && String(e.at ?? "").slice(5, 10) === md && Number(String(e.at).slice(0, 4)) < year)
		.filter((e) => KIND_RANK(e) < 9)
		.sort((a, b) => KIND_RANK(a) - KIND_RANK(b) || b.at.localeCompare(a.at));
	const seen = new Set();
	const out = [];
	for (const e of hits) {
		if (seen.has(e.item_id)) continue;
		seen.add(e.item_id);
		out.push({ event: e, yearsAgo: year - Number(e.at.slice(0, 4)) });
	}
	return out;
}

export function onThisDayLine({ event, yearsAgo }) {
	const when = yearsAgo === 1 ? "A year ago" : `${yearsAgo} years ago`;
	const media = event.media_type;
	const verb =
		event.kind === "added"
			? "you added"
			: event.to_value === "done"
				? media === "book" || media === "comic" ? "you finished reading" : media === "videogame" ? "you finished" : "you finished watching"
				: media === "book" || media === "comic" ? "you started reading" : media === "videogame" ? "you started playing" : "you started watching";
	return `${when} ${verb}`;
}

// ---------- "Still want it" snoozes (device-local) ----------

const SNOOZE_KEY = "backlog:snoozed";
const SNOOZE_DAYS = 182;

export function loadSnoozed() {
	try {
		return new Map(Object.entries(JSON.parse(localStorage.getItem(SNOOZE_KEY) ?? "{}")));
	} catch {
		return new Map();
	}
}

export function snoozeBacklogItem(id) {
	const map = loadSnoozed();
	map.set(id, new Date(Date.now() + SNOOZE_DAYS * 86400000).toISOString());
	try {
		localStorage.setItem(SNOOZE_KEY, JSON.stringify(Object.fromEntries(map)));
	} catch {
		// not remembered — it simply shows again next time
	}
}
